import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import BarcodeScanner from '../components/BarcodeScanner';
import { getPickLists, getPickList, pickItem, completePickList, assignPicker, getUsers } from '../lib/api';
import { recordBinScan, binScanMessage } from '../lib/binScan';
import { HandGrabbing, Barcode, CheckCircle, XCircle, Play, Eye, Camera, Clock, MapPin, SpeakerHigh, SpeakerX, Lock, MagnifyingGlass, Microphone, MicrophoneSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { enqueuePick as enqueueOfflinePick } from '../lib/offlineQueue';
import PickStrategyCard from '../components/PickStrategyCard';
import { useVoicePick, expectedCheckDigits, skuLast4 } from '../lib/voicePick';

const API = process.env.REACT_APP_BACKEND_URL;

function BatchAgeBadge({ days }) {
  if (days === null || days === undefined) return null;
  let color = 'bg-muted text-foreground';
  let label = 'NEW';
  if (days > 90) { color = 'bg-red-100 text-red-800'; label = 'OLD'; }
  else if (days > 30) { color = 'bg-amber-100 text-amber-800'; label = 'AGED'; }
  else if (days > 7) { color = 'bg-blue-100 text-blue-800'; label = 'OK'; }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase ${color}`}>
      <Clock size={10} weight="bold" /> {label} • {days}d
    </span>
  );
}

export default function Picking() {
  const { user, isPicker, isAdmin } = useAuth();
  const { company, refresh: refreshCompany } = useCompany();
  const [pickLists, setPickLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePickList, setActivePickList] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [pickSearch, setPickSearch] = useState('');
  // iter147 — hide-completed toggle (default ON). When ON, the queue shows
  // only actionable work (pending + in_progress). The backend also returns
  // a 24h recent-completed count we surface as a chip in the header.
  const [showCompleted, setShowCompleted] = useState(
    localStorage.getItem('stockaris_picking_show_completed') === 'true'
  );
  const [recentCompleted24h, setRecentCompleted24h] = useState(0);

  // Filter pick lists by user-typed pick number / order id (case-insensitive contains)
  const visiblePickLists = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    if (!q) return pickLists;
    return pickLists.filter((pl) => (pl.pick_list_number || '').toLowerCase().includes(q)
      || (pl.order_ids || []).some((oid) => (oid || '').toLowerCase().includes(q)));
  }, [pickLists, pickSearch]);
  const [users, setUsers] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Company-wide enforcement flags (admin-controlled via /settings/picking)
  const forceRf = !!company?.force_rf_scanning;
  const forceVoice = !!company?.force_voice_picking;
  const skipPack = !!company?.skip_pack_step;

  // Personal toggles respect company-wide enforcement as the floor
  const [rfMode, setRfMode] = useState(
    forceRf || localStorage.getItem('stockaris_rf_mode') === 'true'
  );
  const [voiceMode, setVoiceMode] = useState(
    forceVoice || localStorage.getItem('stockaris_voice_mode') === 'true'
  );

  // iter144 — voice INPUT (speech recognition). Independent from voiceMode
  // which controls the TTS output. Persisted in localStorage so picker
  // preferences survive page reloads.
  const [voiceListen, setVoiceListen] = useState(
    localStorage.getItem('stockaris_voice_listen') === 'true'
  );
  // State machine for the current pick: 'check' waiting for location
  // check digits, 'qty' waiting for the quantity / SKU last-4 confirmation.
  const [voiceStep, setVoiceStep] = useState('check');
  const [voiceFeedback, setVoiceFeedback] = useState('');

  // When admin flips the company-wide toggles, pull personal state up
  useEffect(() => {
    if (forceRf && !rfMode) setRfMode(true);
    if (forceVoice && !voiceMode) setVoiceMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRf, forceVoice]);

  const speak = (text) => {
    if (!voiceMode || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0;
      u.pitch = 1.0;
      u.volume = 1.0;
      window.speechSynthesis.speak(u);
    } catch (e) {
      // Speech synthesis may not be available or blocked
    }
  };

  const toggleVoiceMode = () => {
    if (forceVoice) return; // locked by admin
    const next = !voiceMode;
    setVoiceMode(next);
    localStorage.setItem('stockaris_voice_mode', String(next));
    if (next) {
      speak('Voice pick enabled');
    } else {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    }
  };

  const toggleRfMode = () => {
    if (forceRf) return; // locked by admin
    const next = !rfMode;
    setRfMode(next);
    localStorage.setItem('stockaris_rf_mode', String(next));
  };

  const updatePickingPolicy = async (patch) => {
    setSavingPolicy(true);
    try {
      await axios.patch(`${API}/api/companies/me/settings/picking`, patch, { withCredentials: true });
      await refreshCompany();
      toast.success('Picking policy updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update policy');
    } finally {
      setSavingPolicy(false);
    }
  };

  const fetchData = async () => {
    try {
      const params = {
        hide_completed: !showCompleted,
        ...(isPicker ? { picker_id: user.id } : {}),
      };
      const res = await getPickLists(params);
      // iter147 — backend now wraps the list in {rows, meta} when
      // hide_completed is passed. Fall back to the legacy bare-array
      // shape for any caller that doesn't opt in.
      const data = res.data;
      if (Array.isArray(data)) {
        setPickLists(data);
        setRecentCompleted24h(0);
      } else {
        setPickLists(data.rows || []);
        setRecentCompleted24h(data.meta?.recent_completed_24h || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleShowCompleted = () => {
    const next = !showCompleted;
    setShowCompleted(next);
    localStorage.setItem('stockaris_picking_show_completed', String(next));
  };

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data.filter(u => u.role === 'picker' || u.role === 'manager' || u.role === 'admin'));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { fetchData(); fetchUsers(); }, []);

  // Refetch when the show-completed toggle flips so the queue updates immediately.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCompleted]);

  // Speak the current pick item when it changes (if voice mode enabled).
  // Also includes the expected check digits when voice INPUT is on.
  useEffect(() => {
    if (!activePickList || !voiceMode) return;
    const item = activePickList.items[currentItemIndex];
    if (!item) return;
    const qty = item.quantity_required;
    const loc = item.location_code ? `from ${item.location_code.split('-').join(' ')}` : '';
    const name = item.sku_name || item.sku_code || 'item';
    const checkDigits = voiceListen ? expectedCheckDigits(item.location_code) : null;
    const checkPhrase = checkDigits ? `, say check ${checkDigits.split('').join(' ')}` : '';
    speak(`Pick ${qty} of ${name} ${loc}${checkPhrase}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItemIndex, activePickList?.id, voiceMode, voiceListen]);

  // Reset the voice state machine whenever a new item is in front of the picker.
  useEffect(() => {
    setVoiceStep('check');
    setVoiceFeedback('');
  }, [currentItemIndex, activePickList?.id]);

  const toggleVoiceListen = () => {
    if (typeof window !== 'undefined') {
      const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!Ctor) {
        toast.error('Voice input is not supported in this browser. Try Chrome, Edge, or Safari on a recent phone.');
        return;
      }
    }
    const next = !voiceListen;
    setVoiceListen(next);
    localStorage.setItem('stockaris_voice_listen', String(next));
    if (next) speak('Voice input on');
    else speak('Voice input off');
  };

  // Command handler driven by useVoicePick — implements the qty+check-digit+commands state machine.
  const onVoiceCommand = (cmd) => {
    if (!activePickList) return;
    const item = activePickList.items[currentItemIndex];
    if (!item) return;

    if (cmd.kind === 'cmd') {
      switch (cmd.cmd) {
        case 'repeat': {
          const qty = item.quantity_required;
          const name = item.sku_name || item.sku_code || 'item';
          const loc = item.location_code || '';
          speak(`Pick ${qty} of ${name} from ${loc}`);
          setVoiceFeedback('Repeating…');
          return;
        }
        case 'skip': {
          speak('Skipping');
          setVoiceFeedback('Skipped — advancing');
          if (currentItemIndex < activePickList.items.length - 1) {
            setCurrentItemIndex((p) => p + 1);
          }
          return;
        }
        case 'stop': {
          setVoiceListen(false);
          localStorage.setItem('stockaris_voice_listen', 'false');
          speak('Voice input off');
          return;
        }
        case 'resume':
          // already listening — just acknowledge
          speak('Listening');
          return;
        case 'short': {
          // surface a confirmation toast; full shortage flow lives in Operator Console
          toast.info(`Heard "short ${cmd.n ?? ''}" — finish in the Operator Console to record shortage.`);
          setVoiceFeedback(`Short ${cmd.n ?? ''} heard`);
          return;
        }
        default: return;
      }
    }

    // Check-digit step
    if (voiceStep === 'check') {
      const expected = expectedCheckDigits(item.location_code);
      if (cmd.kind === 'check' || cmd.kind === 'qty') {
        // Accept either "CHECK 04" or bare "04" while in check step
        const heard = cmd.kind === 'check' ? cmd.digits : String(cmd.qty).padStart(2, '0');
        if (expected && heard === expected) {
          setVoiceStep('qty');
          setVoiceFeedback(`Check ${expected} confirmed`);
          speak(`Confirmed, pick ${item.quantity_required}`);
          return;
        }
        setVoiceFeedback(`Heard ${heard} · expected ${expected || '—'}`);
        speak('Check digits wrong');
        return;
      }
      // SKU spoken before check digit — soft reject
      if (cmd.kind === 'sku') {
        setVoiceFeedback('Say check digits first');
        speak('Say check digits first');
      }
      return;
    }

    // Qty step — accept exact qty OR last-4 SKU as verbal scan confirmation
    if (voiceStep === 'qty') {
      if (cmd.kind === 'qty' && cmd.qty === item.quantity_required) {
        setVoiceFeedback(`Qty ${cmd.qty} confirmed`);
        speak('Picked');
        const barcode = item.sku_barcode || item.sku_code || '';
        handleScan(barcode);
        return;
      }
      if (cmd.kind === 'qty') {
        setVoiceFeedback(`Heard ${cmd.qty} · expected ${item.quantity_required}`);
        speak(`Expected ${item.quantity_required}`);
        return;
      }
      if (cmd.kind === 'sku') {
        const expectedSku = skuLast4(item.sku_code);
        if (expectedSku && cmd.last4 === expectedSku) {
          setVoiceFeedback(`SKU ${expectedSku} confirmed`);
          speak('Picked');
          const barcode = item.sku_barcode || item.sku_code || '';
          handleScan(barcode);
          return;
        }
        setVoiceFeedback(`Heard SKU ${cmd.last4} · expected ${expectedSku || '—'}`);
        speak('Wrong SKU');
      }
    }
  };

  const { listening: voiceListening, transcript: voiceTranscript, error: voiceError, supported: voiceSupported } =
    useVoicePick({ enabled: voiceListen && !!activePickList, onCommand: onVoiceCommand });

  const startPicking = async (pickListId) => {
    try {
      const res = await getPickList(pickListId);
      setActivePickList(res.data);
      setCurrentItemIndex(0);
    } catch (e) {
      console.error(e);
    }
  };

  const handleScan = async (barcodeOverride) => {
    const barcode = barcodeOverride || scanInput;
    if (!activePickList || !barcode) return;
    const item = activePickList.items[currentItemIndex];
    const bin = await recordBinScan(activePickList.id, currentItemIndex, barcode, item);
    if (bin) {
      const items = [...activePickList.items];
      items[currentItemIndex] = { ...item, location_code: bin.location_code, zone: bin.zone };
      setActivePickList({ ...activePickList, items });
      setScanInput('');
      setShowScanner(false);
      toast.success(binScanMessage(bin));
      return;
    }
    const payload = {
      item_index: currentItemIndex,
      quantity_picked: item.quantity_required,
      sku_barcode: barcode,
    };

    // Offline fast-path: queue the write and advance optimistically so
    // pickers can keep working in Wi-Fi dead zones.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      enqueueOfflinePick({ pickListId: activePickList.id, payload });
      toast.message('Offline — pick queued. Will sync when back online.');
      setScanInput('');
      setShowScanner(false);
      // Optimistic local advance
      const updatedItems = [...activePickList.items];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        status: 'picked',
        quantity_picked: item.quantity_required,
      };
      setActivePickList({ ...activePickList, items: updatedItems });
      if (currentItemIndex < activePickList.items.length - 1) {
        setCurrentItemIndex((prev) => prev + 1);
      }
      return;
    }

    try {
      const res = await pickItem(activePickList.id, payload);
      setScanInput('');
      setShowScanner(false);
      if (res?.data?.warning === 'scan_mismatch') {
        toast.warning('Scanned barcode did not match expected SKU — logged for accuracy review.');
      }
      if (currentItemIndex < activePickList.items.length - 1) {
        setCurrentItemIndex(prev => prev + 1);
        const res = await getPickList(activePickList.id);
        setActivePickList(res.data);
      } else {
        const res2 = await completePickList(activePickList.id);
        const data = res2?.data || {};
        if (data.pack_step_skipped) {
          const n = (data.orders_advanced_to_packed || []).length;
          toast.success(`Pick complete — ${n} order${n === 1 ? '' : 's'} sent straight to Shipping (pack step skipped).`);
        } else {
          const created = data.pack_tasks_count || 0;
          if (created > 0) {
            toast.success(`Pick complete — ${created} pack task${created === 1 ? '' : 's'} ready in Packing.`);
          } else {
            toast.success('Pick list completed.');
          }
        }
        setActivePickList(null);
        fetchData();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && detail && typeof detail === 'object' && detail.error === 'wrong_sku_scanned') {
        const expected = detail.expected_sku_code || '—';
        toast.error(`Wrong SKU scanned — expected ${expected}. Logged to scan accuracy.`);
        setScanInput('');
      } else if (!navigator.onLine || e?.message === 'Network Error') {
        // Lost connection mid-request — queue it
        enqueueOfflinePick({ pickListId: activePickList.id, payload });
        toast.message('Connection lost — pick queued. Will sync when back online.');
        setScanInput('');
        if (currentItemIndex < activePickList.items.length - 1) {
          setCurrentItemIndex((prev) => prev + 1);
        }
      } else {
        console.error(e);
      }
    }
  };

  const handleAssign = async (pickListId, pickerId) => {
    try {
      await assignPicker(pickListId, pickerId);
      setShowAssign(null);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  if (activePickList) {
    const item = activePickList.items[currentItemIndex];
    const progress = (currentItemIndex / activePickList.items.length) * 100;
    const pickedCount = activePickList.items.filter(i => i.status === 'picked').length;

    return (
      <Layout>
        <div data-testid="picking-active" className="p-4 md:p-6 max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold">{activePickList.pick_list_number}</h1>
              <Button variant="outline" onClick={() => setActivePickList(null)}>Exit</Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{pickedCount}/{activePickList.items.length} items picked</span>
            </div>
            <Progress value={progress} className="h-2 mt-2" />
          </div>

          {/* Current Item Card */}
          <div className="bg-card border-2 border-brand p-4 md:p-6 mb-6">
            <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
              <div className="overline">Item {currentItemIndex + 1} of {activePickList.items.length}</div>
              {activePickList.route_optimized && (
                <span className="text-xs font-bold text-brand uppercase">Route Optimized</span>
              )}
            </div>
            <div className="flex items-start gap-3 mb-2">
              {item?.image_base64 && (
                <img
                  src={item.image_base64}
                  alt={item.sku_code || 'SKU'}
                  className="w-16 h-16 md:w-20 md:h-20 shrink-0 object-cover border border-border"
                  data-testid="pick-item-image"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xl md:text-2xl font-bold break-words">{item?.sku_name || item?.sku_code || 'Unknown Item'}</p>
                {item?.sku_name && item?.sku_code && (
                  <p className="text-sm font-mono text-muted-foreground break-all">{item.sku_code}</p>
                )}
              </div>
            </div>
            
            {/* Location */}
            {item?.location_code && (
              <div className="flex items-center gap-2 mb-3 p-3 bg-brand/5 border border-brand/20">
                <MapPin size={20} weight="fill" className="text-brand shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Pick Location</p>
                  <p className="font-mono font-bold text-brand text-base md:text-lg break-all">{item.location_code}</p>
                </div>
              </div>
            )}
            
            {/* Batch Info */}
            {item?.batch_number && (
              <div className="flex items-center gap-2 mb-3 text-sm flex-wrap">
                <span className="text-muted-foreground">Batch:</span>
                <span className="font-mono font-bold break-all">{item.batch_number}</span>
                <BatchAgeBadge days={item.batch_age_days} />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-muted/50 p-3 rounded">
                <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Quantity</p>
                <p className="text-3xl md:text-4xl font-bold">
                  {item?.quantity_required}
                  {item?.unit_of_measure && item.unit_of_measure !== 'each' && (
                    <span className="text-base ml-1 text-muted-foreground font-mono uppercase" data-testid="pick-base-uom">{item.unit_of_measure}</span>
                  )}
                </p>
                {/* iter164 — show operator-entered UOM context when the order
                    line used a non-base UOM (e.g. customer ordered 1 case = 12 L). */}
                {item?.order_line_uom && item?.order_line_quantity_in_uom && item.order_line_uom !== (item.unit_of_measure || 'each') && (
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono uppercase" data-testid="pick-order-uom">
                    customer: {item.order_line_quantity_in_uom} {item.order_line_uom}
                  </p>
                )}
              </div>
              <div className="bg-muted/50 p-3 rounded">
                <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Status</p>
                <p className={`text-lg md:text-2xl font-bold ${item?.status === 'picked' ? 'text-green-600' : 'text-amber-600'}`}>
                  {item?.status?.toUpperCase()}
                </p>
              </div>
            </div>
          </div>

          {/* Scan Input */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="overline">Scan Mode</label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={toggleVoiceMode}
                  disabled={forceVoice}
                  data-testid="voice-mode-toggle"
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border ${voiceMode ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'} ${forceVoice ? 'opacity-80 cursor-not-allowed' : ''}`}
                  title={forceVoice ? 'Locked on by admin' : 'Read pick instructions aloud'}
                >
                  {forceVoice ? <Lock size={12} weight="fill" /> : (voiceMode ? <SpeakerHigh size={12} weight="fill" /> : <SpeakerX size={12} />)}
                  {voiceMode ? 'VOICE ON' : 'VOICE OFF'}{forceVoice ? ' · LOCKED' : ''}
                </button>
                <button
                  onClick={toggleVoiceListen}
                  data-testid="voice-listen-toggle"
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border ${voiceListen ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-card text-muted-foreground border-border'}`}
                  title="Hands-free voice picking · speak check digits and qty"
                >
                  {voiceListen ? <Microphone size={12} weight="fill" /> : <MicrophoneSlash size={12} />}
                  {voiceListen ? (voiceListening ? 'LISTEN ●' : 'LISTEN …') : 'LISTEN OFF'}
                </button>
                <button
                  onClick={toggleRfMode}
                  disabled={forceRf}
                  data-testid="rf-mode-toggle"
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border ${rfMode ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'} ${forceRf ? 'opacity-80 cursor-not-allowed' : ''}`}
                  title={forceRf ? 'Locked on by admin' : ''}
                >
                  {forceRf && <Lock size={12} weight="fill" />}
                  {rfMode ? 'RF MODE ON' : 'RF MODE OFF'}{forceRf ? ' · LOCKED' : ''}
                </button>
              </div>
            </div>
            {(forceRf || forceVoice) && (
              <div
                data-testid="pick-enforcement-banner"
                className="text-[11px] uppercase tracking-widest font-bold text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 flex items-center gap-2"
              >
                <Lock size={12} weight="fill" />
                {forceRf && forceVoice
                  ? 'RF scanning + voice picking required · manual confirmation disabled'
                  : forceRf
                    ? 'RF scanning required · manual confirmation disabled'
                    : 'Voice picking required · manual confirmation disabled'}
              </div>
            )}
            {voiceListen && (
              <div
                data-testid="voice-input-banner"
                className={`border px-3 py-3 flex flex-col gap-2 ${voiceError ? 'bg-rose-50 border-rose-200' : voiceListening ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    {voiceListening
                      ? <span className="inline-block w-2 h-2 rounded-full bg-emerald-600 animate-pulse" />
                      : <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />}
                    <span className="text-[11px] font-bold uppercase tracking-widest">
                      {voiceError ? 'Mic error' : voiceListening ? 'Listening' : 'Standby'}
                    </span>
                  </div>
                  <span className="text-[11px] uppercase tracking-widest font-bold" data-testid="voice-step-label">
                    {voiceStep === 'check' ? 'Say check digits' : 'Say quantity or SKU last 4'}
                  </span>
                </div>
                {(() => {
                  const item = activePickList.items[currentItemIndex];
                  const check = expectedCheckDigits(item?.location_code);
                  const last4 = skuLast4(item?.sku_code);
                  return (
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                      <div data-testid="voice-expected-check">
                        <span className="text-muted-foreground">Check:</span>{' '}
                        <span className="font-mono font-bold text-base">{check ?? '—'}</span>
                      </div>
                      <div className="text-muted-foreground">·</div>
                      <div data-testid="voice-expected-qty">
                        <span className="text-muted-foreground">Qty:</span>{' '}
                        <span className="font-mono font-bold text-base">{item?.quantity_required ?? '—'}</span>
                      </div>
                      <div className="text-muted-foreground">·</div>
                      <div data-testid="voice-expected-sku">
                        <span className="text-muted-foreground">SKU end:</span>{' '}
                        <span className="font-mono font-bold text-base">{last4 ?? '—'}</span>
                      </div>
                    </div>
                  );
                })()}
                {voiceTranscript && (
                  <div className="text-[11px] font-mono text-muted-foreground" data-testid="voice-transcript">
                    Heard: <span className="text-foreground">&ldquo;{voiceTranscript}&rdquo;</span>
                  </div>
                )}
                {voiceFeedback && (
                  <div className="text-xs font-bold" data-testid="voice-feedback">{voiceFeedback}</div>
                )}
                {voiceError && (
                  <div className="text-[11px] text-rose-700" data-testid="voice-error">
                    {voiceError === 'not-allowed' || voiceError === 'service-not-allowed'
                      ? 'Microphone permission denied · enable mic in browser settings'
                      : voiceError === 'not_supported'
                        ? 'Speech recognition not supported in this browser'
                        : `Mic error: ${voiceError}`}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground italic leading-tight">
                  Commands: &ldquo;repeat&rdquo;, &ldquo;skip&rdquo;, &ldquo;stop&rdquo;. Say a number for qty or NATO-spell the last 4 of the SKU (e.g. &ldquo;alpha seven two three&rdquo;).
                </p>
              </div>
            )}
            {!voiceSupported && voiceListen && (
              <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2" data-testid="voice-unsupported">
                Speech recognition is not available in this browser.
              </div>
            )}
            {!rfMode && !forceRf && (
              <Button
                onClick={() => setShowScanner(true)}
                data-testid="open-scanner-btn"
                className="w-full h-16 text-lg bg-brand hover:bg-brand/90 font-bold"
              >
                <Camera size={24} className="mr-2" /> Scan with Camera
              </Button>
            )}
            <div className="relative">
              <Barcode size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                placeholder={
                  forceRf
                    ? 'RF scan required — no manual entry'
                    : rfMode
                      ? 'Trigger RF scanner...'
                      : 'Or enter barcode manually...'
                }
                className={`h-16 pl-14 text-lg font-mono ${rfMode || forceRf ? 'border-2 border-brand bg-blue-50' : ''}`}
                data-testid="pick-scan-input"
                autoFocus={rfMode || forceRf}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                inputMode="text"
              />
            </div>
            {!(forceRf || forceVoice) && (
              <Button onClick={() => handleScan()} data-testid="pick-confirm-button" variant="outline" className="w-full h-16 text-lg font-bold">
                <CheckCircle size={24} className="mr-2" /> Confirm Pick
              </Button>
            )}
          </div>
        </div>
        {showScanner && (
          <BarcodeScanner
            onScan={(code) => handleScan(code)}
            onClose={() => setShowScanner(false)}
            title="Scan SKU Barcode"
          />
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="picking-page" className="p-4 md:p-6">
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Picking</h1>
            <p className="text-muted-foreground">Manage pick lists and complete picks</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {recentCompleted24h > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-200"
                data-testid="recent-completed-chip"
                title="Completed in the last 24 hours"
              >
                <CheckCircle size={12} weight="fill" /> {recentCompleted24h} completed · 24h
              </span>
            )}
            <button
              onClick={toggleShowCompleted}
              data-testid="show-completed-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${showCompleted ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'}`}
              title="Show or hide finished pick lists"
            >
              <Eye size={12} weight={showCompleted ? 'fill' : 'regular'} />
              {showCompleted ? 'Showing completed' : 'Hide completed'}
            </button>
          </div>
        </div>

        {isAdmin && <PickStrategyCard />}

        {isAdmin && (
          <div
            data-testid="picking-policy-card"
            className="mb-6 border border-border bg-card p-4 space-y-3"
          >
            <div className="flex items-center gap-2">
              <Lock size={14} weight="fill" className="text-brand" />
              <h2 className="text-xs font-bold uppercase tracking-widest">Picking enforcement · admin only</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              Company-wide policy. When either is ON, the "Confirm Pick" button is hidden on the floor so pickers
              can only advance via a real scan event — no manual overrides.
            </p>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold">Force RF / camera scanning</div>
                <div className="text-[11px] text-muted-foreground">Pickers must scan every item · manual text entry is treated as a scan</div>
              </div>
              <Switch
                checked={forceRf}
                disabled={savingPolicy}
                onCheckedChange={(v) => updatePickingPolicy({ force_rf_scanning: v })}
                data-testid="policy-force-rf"
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-bold">Force voice picking</div>
                <div className="text-[11px] text-muted-foreground">Voice mode always on · toggle hidden on the floor</div>
              </div>
              <Switch
                checked={forceVoice}
                disabled={savingPolicy}
                onCheckedChange={(v) => updatePickingPolicy({ force_voice_picking: v })}
                data-testid="policy-force-voice"
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2 pt-3 border-t border-border">
              <div>
                <div className="text-sm font-bold">Skip pack step</div>
                <div className="text-[11px] text-muted-foreground">
                  Lean mode: completed picks go straight to Shipping · no pack tasks created
                </div>
              </div>
              <Switch
                checked={skipPack}
                disabled={savingPolicy}
                onCheckedChange={(v) => updatePickingPolicy({ skip_pack_step: v })}
                data-testid="policy-skip-pack"
              />
            </div>
          </div>
        )}

        <div className="grid gap-4">
          {/* Job-number search */}
          <div className="relative" data-testid="picking-search-bar">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="picking-search"
              placeholder="Search pick number…"
              value={pickSearch || ''}
              onChange={(e) => setPickSearch(e.target.value)}
              className="pl-9 h-10 font-mono"
              autoComplete="off"
            />
          </div>
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 animate-pulse"></div>)
          ) : visiblePickLists.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="picking-empty">
              <HandGrabbing size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-muted-foreground mb-3">
                {pickSearch
                  ? `No pick lists match "${pickSearch}".`
                  : !showCompleted
                    ? 'No active pick lists in the queue. Finished work is hidden — toggle "Showing completed" above to view history.'
                    : 'No pick lists available'}
              </p>
              {!pickSearch && !showCompleted && recentCompleted24h > 0 && (
                <button
                  onClick={toggleShowCompleted}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                  data-testid="empty-view-completed-btn"
                >
                  <Eye size={12} weight="fill" /> View {recentCompleted24h} completed today
                </button>
              )}
            </div>
          ) : visiblePickLists.map(pl => {
            const pickedCount = pl.items?.filter(i => i.status === 'picked').length || 0;
            const totalItems = pl.items?.length || 0;
            const progress = totalItems > 0 ? (pickedCount / totalItems) * 100 : 0;
            
            return (
              <div key={pl.id} className="bg-card border border-border rounded-lg p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-mono font-bold text-lg">{pl.pick_list_number}</p>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${
                        pl.status === 'completed' ? 'bg-green-100 text-green-800' :
                        pl.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {pl.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{totalItems} items</span>
                      <span>{pl.order_ids?.length || 0} orders</span>
                      {pl.picker_id && <span>Assigned: {users.find(u => u.id === pl.picker_id)?.name || 'Unknown'}</span>}
                    </div>
                    <Progress value={progress} className="h-2 mt-3" />
                  </div>
                  <div className="flex gap-2">
                    {!pl.picker_id && !isPicker && (
                      <>
                        <Button variant="outline" onClick={() => setShowAssign(pl.id)}>Assign</Button>
                        <Dialog open={showAssign === pl.id} onOpenChange={() => setShowAssign(null)}>
                          <DialogContent className="border border-border rounded-none">
                            <DialogHeader>
                              <DialogTitle>Assign Picker</DialogTitle>
                            </DialogHeader>
                            <Select onValueChange={(v) => handleAssign(pl.id, v)}>
                              <SelectTrigger><SelectValue placeholder="Select picker" /></SelectTrigger>
                              <SelectContent>
                                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                    {pl.status !== 'completed' && (pl.picker_id === user.id || !isPicker) && (
                      <Button onClick={() => startPicking(pl.id)} data-testid="start-picking-btn" className="bg-brand hover:bg-brand/90">
                        <Play size={18} className="mr-2" /> Start Picking
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
